import React, { useState, useEffect } from 'react';
import { X, Calendar, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient, ReportType, ReportSchedule, CreateScheduleRequest, UpdateScheduleRequest, ReportParameters } from '../../services/api';
import { validate } from '../../utils/validation';

interface ScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    schedule: ReportSchedule | null;
    user: any;
    onSuccess: () => void;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, schedule, user, onSuccess }) => {
    const isEdit = !!schedule;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ reportName?: string; minRiskScore?: string; maxRiskScore?: string }>({});

    // Form state
    const [reportType, setReportType] = useState<ReportType>(schedule?.type || 'compliance');
    const [reportName, setReportName] = useState(schedule?.reportName || '');
    const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>(schedule?.frequency || 'weekly');
    const [isOrgWide, setIsOrgWide] = useState(schedule?.isOrgWide || false);

    // Schedule configuration
    const [startDateTime, setStartDateTime] = useState<string>(schedule?.startDateTime || '');
    const [dayOfWeek, setDayOfWeek] = useState<number>(schedule?.dayOfWeek ?? 1); // Default to Monday
    const [dayOfMonth, setDayOfMonth] = useState<number>(schedule?.dayOfMonth ?? 1); // Default to 1st
    const [timeOfDay, setTimeOfDay] = useState<string>(schedule?.timeOfDay || '09:00');

    // Compliance parameters
    const [consentStatus, setConsentStatus] = useState<'all' | 'active' | 'expired' | 'expiring_soon' | 'no_consent'>('all');
    const [riskLevels, setRiskLevels] = useState<string[]>([]);

    // Risk Analysis parameters
    const [minRiskScore, setMinRiskScore] = useState<number | null>(null);
    const [maxRiskScore, setMaxRiskScore] = useState<number | null>(null);

    // Email Delivery parameters
    const [emailStatus, setEmailStatus] = useState<'all' | 'delivered' | 'failed' | 'bounced'>('all');

    // DBS Check parameters
    const [dbsCheckStatus, setDbsCheckStatus] = useState<'all' | 'clear' | 'not_clear' | 'in_progress' | 'pending' | 'failed'>('all');
    const [dbsCheckType, setDbsCheckType] = useState<'all' | 'basic' | 'standard' | 'enhanced'>('all');

    // Load parameters from existing schedule
    useEffect(() => {
        if (schedule?.parameters) {
            const params = schedule.parameters;

            // Compliance parameters
            if (params.consentStatus) setConsentStatus(params.consentStatus);
            if (params.riskLevels) setRiskLevels(params.riskLevels);

            // Risk Analysis parameters
            if (params.minRiskScore !== undefined) setMinRiskScore(params.minRiskScore);
            if (params.maxRiskScore !== undefined) setMaxRiskScore(params.maxRiskScore);

            // Email Delivery parameters
            if (params.emailStatus) setEmailStatus(params.emailStatus);

            // DBS Check parameters
            if (params.dbsStatus) setDbsCheckStatus(params.dbsStatus as any);
            if (params.dbsType) setDbsCheckType(params.dbsType as any);
        }
    }, [schedule]);

    const handleRiskLevelToggle = (level: string) => {
        if (riskLevels.includes(level)) {
            setRiskLevels(riskLevels.filter(l => l !== level));
        } else {
            setRiskLevels([...riskLevels, level]);
        }
    };

    const buildParameters = (): ReportParameters => {
        const parameters: ReportParameters = {};

        switch (reportType) {
            case 'compliance':
                parameters.consentStatus = consentStatus;
                if (riskLevels.length > 0) {
                    parameters.riskLevels = riskLevels;
                }
                break;

            case 'risk_analysis':
                if (minRiskScore !== null) parameters.minRiskScore = minRiskScore;
                if (maxRiskScore !== null) parameters.maxRiskScore = maxRiskScore;
                if (riskLevels.length > 0) parameters.riskLevels = riskLevels;
                break;

            case 'email_delivery':
                parameters.emailStatus = emailStatus;
                break;

            case 'licence_check':
            case 'audit_activity':
                break;

            case 'dbs_check':
                if (dbsCheckStatus !== 'all') parameters.dbsStatus = dbsCheckStatus;
                if (dbsCheckType !== 'all') parameters.dbsType = dbsCheckType;
                break;
        }

        return parameters;
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        const nameErr = validate.required(reportName.trim(), 'Report name');
        if (nameErr) newErrors.reportName = nameErr;

        if (reportType === 'risk_analysis') {
            if (minRiskScore !== null) {
                const minErr = validate.riskScore(minRiskScore, 'Min risk score');
                if (minErr) newErrors.minRiskScore = minErr;
            }
            if (maxRiskScore !== null) {
                const maxErr = validate.riskScore(maxRiskScore, 'Max risk score');
                if (maxErr) newErrors.maxRiskScore = maxErr;
            }
            if (minRiskScore !== null && maxRiskScore !== null && !newErrors.minRiskScore && !newErrors.maxRiskScore) {
                if (minRiskScore > maxRiskScore) {
                    newErrors.maxRiskScore = 'Max risk score must be greater than or equal to min';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setIsSubmitting(true);
            const parameters = buildParameters();

            if (isEdit && schedule) {
                // Update existing schedule
                const updateData: UpdateScheduleRequest = {
                    reportName,
                    parameters,
                    frequency,
                    isOrgWide: user.role === 'admin' ? isOrgWide : schedule.isOrgWide,
                    startDateTime: startDateTime || null,
                    dayOfWeek: frequency === 'weekly' ? dayOfWeek : null,
                    dayOfMonth: (frequency === 'monthly' || frequency === 'quarterly') ? dayOfMonth : null,
                    timeOfDay: timeOfDay || null,
                    timezone: 'UTC'
                };
                await apiClient.updateReportSchedule(schedule.id, updateData);
                toast.success('Schedule updated successfully');
            } else {
                // Create new schedule
                const createData: CreateScheduleRequest = {
                    reportType,
                    reportName,
                    parameters,
                    frequency,
                    isOrgWide: user.role === 'admin' ? isOrgWide : false,
                    startDateTime: startDateTime || null,
                    dayOfWeek: frequency === 'weekly' ? dayOfWeek : null,
                    dayOfMonth: (frequency === 'monthly' || frequency === 'quarterly') ? dayOfMonth : null,
                    timeOfDay: timeOfDay || null,
                    timezone: 'UTC'
                };
                await apiClient.createReportSchedule(createData);
                toast.success('Schedule created successfully');
            }

            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.message || `Failed to ${isEdit ? 'update' : 'create'} schedule`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                            <Calendar className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                {isEdit ? 'Edit Schedule' : 'Create Schedule'}
                            </h2>
                            <p className="text-sm text-gray-500">Configure automated report generation</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Report Type - Disabled when editing */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Report Type {isEdit && <span className="text-xs text-gray-500">(cannot be changed)</span>}
                        </label>
                        <select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value as ReportType)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            disabled={isEdit}
                        >
                            <option value="compliance">Compliance Report</option>
                            <option value="risk_analysis">Risk Analysis Report</option>
                            <option value="licence_check">Licence Check Report</option>
                            <option value="email_delivery">Email Delivery Report</option>
                            <option value="audit_activity">Audit Activity Report</option>
                            {user?.organisation?.trustidEnabled && <option value="dbs_check">DBS Check Report</option>}
                        </select>
                    </div>

                    {/* Report Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Report Name *
                        </label>
                        <input
                            type="text"
                            value={reportName}
                            onChange={(e) => { setReportName(e.target.value); if (errors.reportName) setErrors(p => ({ ...p, reportName: undefined })); }}
                            placeholder="e.g., Weekly Compliance Report"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.reportName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        />
                        {errors.reportName && <p className="mt-1 text-sm text-red-600">{errors.reportName}</p>}
                    </div>

                    {/* Frequency */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Frequency *
                        </label>
                        <select
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value as any)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}

                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                        </select>
                    </div>

                    {/* Schedule Timing Configuration */}
                    <div className="space-y-4 pt-4">
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">Schedule Timing</h3>

                        {/* Start Date/Time */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Start Date (Optional)
                            </label>
                            <input
                                type="datetime-local"
                                value={startDateTime}
                                onChange={(e) => setStartDateTime(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                min={new Date().toISOString().slice(0, 16)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                When should this schedule start? Leave empty to start from the next occurrence.
                            </p>
                        </div>

                        {/* Day of Week (Weekly only) */}
                        {frequency === 'weekly' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Day of Week *
                                </label>
                                <select
                                    value={dayOfWeek}
                                    onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                >
                                    <option value="0">Sunday</option>
                                    <option value="1">Monday</option>
                                    <option value="2">Tuesday</option>
                                    <option value="3">Wednesday</option>
                                    <option value="4">Thursday</option>
                                    <option value="5">Friday</option>
                                    <option value="6">Saturday</option>
                                </select>
                            </div>
                        )}

                        {/* Day of Month (Monthly/Quarterly only) */}
                        {(frequency === 'monthly' || frequency === 'quarterly') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Day of Month *
                                </label>
                                <select
                                    value={dayOfMonth}
                                    onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                >
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                        <option key={day} value={day}>
                                            {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of the month
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    If the selected day doesn't exist in a month (e.g., 31st in February), it will run on the last day of that month.
                                </p>
                            </div>
                        )}

                        {/* Time of Day */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Time of Day *
                            </label>
                            <input
                                type="time"
                                value={timeOfDay}
                                onChange={(e) => setTimeOfDay(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                What time should this report run? (24-hour format)
                            </p>
                        </div>
                    </div>

                    {/* Report-specific Parameters */}
                    {reportType === 'compliance' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-sm font-semibold text-gray-900">Compliance Filters</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Consent Status
                                </label>
                                <select
                                    value={consentStatus}
                                    onChange={(e) => setConsentStatus(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    
                                >
                                    <option value="all">All Drivers</option>
                                    <option value="active">Active Consent Only</option>
                                    <option value="expired">Expired Consent Only</option>
                                    <option value="expiring_soon">Expiring Soon (30 days)</option>
                                    <option value="no_consent">No Consent</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Risk Levels (Optional)
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {['low', 'medium', 'high'].map((level) => (
                                        <button
                                            key={level}
                                            type="button"
                                            onClick={() => handleRiskLevelToggle(level)}
                                            className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                                riskLevels.includes(level)
                                                    ? 'border-green-500 bg-green-50 text-green-900'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                            }`}
                                        >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {reportType === 'risk_analysis' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-sm font-semibold text-gray-900">Risk Analysis Filters</h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Min Risk Score
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={minRiskScore ?? ''}
                                        onChange={(e) => { setMinRiskScore(e.target.value ? parseInt(e.target.value) : null); if (errors.minRiskScore) setErrors(p => ({ ...p, minRiskScore: undefined })); }}
                                        placeholder="0"
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.minRiskScore ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    />
                                    {errors.minRiskScore && <p className="mt-1 text-sm text-red-600">{errors.minRiskScore}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Max Risk Score
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={maxRiskScore ?? ''}
                                        onChange={(e) => { setMaxRiskScore(e.target.value ? parseInt(e.target.value) : null); if (errors.maxRiskScore) setErrors(p => ({ ...p, maxRiskScore: undefined })); }}
                                        placeholder="100"
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.maxRiskScore ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    />
                                    {errors.maxRiskScore && <p className="mt-1 text-sm text-red-600">{errors.maxRiskScore}</p>}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Risk Levels
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {['low', 'medium', 'high'].map((level) => (
                                        <button
                                            key={level}
                                            type="button"
                                            onClick={() => handleRiskLevelToggle(level)}
                                            className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                                riskLevels.includes(level)
                                                    ? 'border-green-500 bg-green-50 text-green-900'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                            }`}
                                        >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {reportType === 'email_delivery' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-sm font-semibold text-gray-900">Email Delivery Filters</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Status
                                </label>
                                <select
                                    value={emailStatus}
                                    onChange={(e) => setEmailStatus(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    
                                >
                                    <option value="all">All Emails</option>
                                    <option value="delivered">Delivered Only</option>
                                    <option value="failed">Failed Only</option>
                                    <option value="bounced">Bounced Only</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {reportType === 'dbs_check' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-sm font-semibold text-gray-900">DBS Check Filters</h3>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">DBS Status</label>
                                <select
                                    value={dbsCheckStatus}
                                    onChange={(e) => setDbsCheckStatus(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="clear">Clear</option>
                                    <option value="not_clear">Not Clear</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Check Type</label>
                                <select
                                    value={dbsCheckType}
                                    onChange={(e) => setDbsCheckType(e.target.value as any)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                >
                                    <option value="all">All Types</option>
                                    <option value="basic">Basic</option>
                                    <option value="standard">Standard</option>
                                    <option value="enhanced">Enhanced</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Organisation-wide toggle (admin only) */}
                    {user.role === 'admin' && (
                        <div className="border-t pt-4">
                            <label className="flex items-start space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isOrgWide}
                                    onChange={(e) => setIsOrgWide(e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-gray-300 accent-[#69BE28]"
                                />
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">
                                        Organisation-wide Report
                                    </span>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Make this report visible to all users in the organisation
                                    </p>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Info Box */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-600">
                            <p className="font-medium mb-1 text-gray-700">Automated Report Generation</p>
                            <p>
                                This schedule will automatically generate reports based on your selected frequency.
                                Reports will be available in the Report History tab and you'll receive notifications when they're ready.
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 border-t pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2 disabled:opacity-50"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>{isEdit ? 'Update Schedule' : 'Create Schedule'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ScheduleModal;
